import React, { useRef, useState, useCallback } from 'react';
import MessageActionBar from './MessageActionBar';
import type { ReplyTarget } from '../../types/chat';

const SWIPE_THRESHOLD = 56;
const SWIPE_MAX_OFFSET = 72;

interface ChatMessageShellProps {
  messageId: string;
  alignRight: boolean;
  onReply: (target: ReplyTarget) => void;
  onReact: (emoji: string) => void;
  onReport?: () => void;
  modSlot?: React.ReactNode;
  replyTarget: ReplyTarget;
  enableSwipe?: boolean;
  className?: string;
  children: React.ReactNode;
}

export default function ChatMessageShell({
  messageId,
  alignRight,
  onReply,
  onReact,
  onReport,
  modSlot,
  replyTarget,
  enableSwipe = false,
  className = '',
  children,
}: ChatMessageShellProps) {
  const [swipeOffset, setSwipeOffset] = useState(0);
  const touchStartX = useRef(0);
  const touchStartY = useRef(0);
  const swipeActive = useRef(false);

  const handleReply = useCallback(() => {
    onReply(replyTarget);
  }, [onReply, replyTarget]);

  const handleTouchStart = useCallback((event: React.TouchEvent) => {
    if (!enableSwipe) return;
    touchStartX.current = event.touches[0].clientX;
    touchStartY.current = event.touches[0].clientY;
    swipeActive.current = false;
  }, [enableSwipe]);

  const handleTouchMove = useCallback((event: React.TouchEvent) => {
    if (!enableSwipe) return;

    const deltaX = event.touches[0].clientX - touchStartX.current;
    const deltaY = event.touches[0].clientY - touchStartY.current;

    if (!swipeActive.current) {
      if (Math.abs(deltaX) < 8 || Math.abs(deltaX) <= Math.abs(deltaY)) {
        return;
      }
      swipeActive.current = true;
    }

    if (deltaX > 0) {
      setSwipeOffset(Math.min(deltaX, SWIPE_MAX_OFFSET));
    } else {
      setSwipeOffset(0);
    }
  }, [enableSwipe]);

  const handleTouchEnd = useCallback(() => {
    if (!enableSwipe) return;

    if (swipeOffset >= SWIPE_THRESHOLD) {
      handleReply();
    }

    setSwipeOffset(0);
    swipeActive.current = false;
  }, [enableSwipe, handleReply, swipeOffset]);

  const actionAlign = alignRight ? 'start' : 'end';
  const showSwipeHint = enableSwipe && swipeOffset > 12;

  return (
    <div
      className={`message-swipe-container ${alignRight ? 'align-right' : 'align-left'} ${modSlot ? 'message-swipe-container--mod-tools' : ''} ${className}`.trim()}
      data-message-id={messageId}
      style={swipeOffset > 0 ? { transform: `translateX(${swipeOffset}px)` } : undefined}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onTouchCancel={handleTouchEnd}
    >
      {enableSwipe && (
        <div
          className={`message-swipe-hint ${showSwipeHint ? 'visible' : ''}`}
          aria-hidden="true"
        >
          ↩
        </div>
      )}

      {children}

      <MessageActionBar onReply={handleReply} onReact={onReact} onReport={onReport} modSlot={modSlot} align={actionAlign} />
    </div>
  );
}
